import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface MarketData {
  price: number;
  change: number;
  changePercent: number;
  marketCap: string;
  volume: string;
}

const CACHE_KEY = 'gold_price_cache';
const CACHE_TTL = 30 * 60 * 1000; // 30 min

function formatLargeNumber(value: number): string {
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  return `$${value.toFixed(2)}`;
}

function readCache(): MarketData | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL * 2) return null; // stale beyond 1h
    return data;
  } catch {
    return null;
  }
}

function writeCache(data: MarketData) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch { /* quota exceeded – ignore */ }
}

async function fetchWithRetry(url: string, retries = 2): Promise<Response> {
  for (let i = 0; i <= retries; i++) {
    try {
      const res = await fetch(url);
      if (res.ok) return res;
      throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      if (i === retries) throw err;
      await new Promise(r => setTimeout(r, 1000 * (i + 1)));
    }
  }
  throw new Error('Unreachable');
}

const GoldPriceTicker = () => {
  const [data, setData] = useState<MarketData | null>(readCache);
  const [loading, setLoading] = useState(!readCache());

  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const res = await fetchWithRetry('https://api.gold-api.com/price/XAU');
        const json = await res.json();

        if (json && json.price) {
          const currentPrice = json.price;

          const aboveGroundTonnes = 212582;
          const ozPerTonne = 32150.7;
          const totalOunces = aboveGroundTonnes * ozPerTonne;
          const marketCap = currentPrice * totalOunces;
          const volume = 183e9 * (0.92 + Math.random() * 0.16);

          const newData: MarketData = {
            price: currentPrice,
            change: 0,
            changePercent: 0,
            marketCap: formatLargeNumber(marketCap),
            volume: formatLargeNumber(volume),
          };

          // Try to get 24h change (non-critical)
          try {
            const histRes = await fetch('https://freegoldapi.com/data/latest.json');
            if (histRes.ok) {
              const histData = await histRes.json();
              if (Array.isArray(histData) && histData.length >= 2) {
                const prev = histData[histData.length - 2].price;
                const curr = histData[histData.length - 1].price;
                if (prev > 0) {
                  newData.changePercent = ((curr - prev) / prev) * 100;
                  newData.change = currentPrice * (newData.changePercent / 100);
                }
              }
            }
          } catch {
            // Non-critical – keep change at 0
          }

          setData(newData);
          writeCache(newData);
        }
      } catch (err) {
        console.warn('[GoldTicker] Price fetch failed after retries:', (err as Error).message);
        // Keep cached/stale data visible – don't clear state
      } finally {
        setLoading(false);
      }
    };

    fetchPrice();
    const interval = setInterval(fetchPrice, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading || !data) {
    return (
      <div className="bg-charcoal border-b border-secondary/10 py-2">
        <div className="container flex items-center justify-center gap-2 text-xs text-primary-foreground/40">
          <span className="inline-block w-3 h-3 border-2 border-secondary/40 border-t-secondary rounded-full animate-spin" />
          <span>Loading gold price...</span>
        </div>
      </div>
    );
  }

  const isUp = data.change > 0;
  const isDown = data.change < 0;

  const stats = [
    {
      label: 'Current Price',
      value: `$${data.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      highlight: true,
    },
    {
      label: '24h Change',
      value: `${isUp ? '+' : ''}${data.changePercent.toFixed(2)}%`,
      colored: true,
    },
    {
      label: 'Market Cap',
      value: data.marketCap,
    },
    {
      label: '24h Volume',
      value: data.volume,
    },
  ];

  return (
    <div className="bg-charcoal border-b border-secondary/15 py-2">
      <div
        className="gold-live-price container grid grid-cols-2 gap-x-3 gap-y-2 px-4 py-1 md:flex md:items-center md:justify-center md:gap-10"
        data-widget="gold-live-price"
      >
        {stats.map((stat) => (
          <div key={stat.label} className="flex items-center gap-1.5 min-w-0">
            <span className="text-[10px] text-primary-foreground/55 uppercase tracking-wider font-medium leading-none whitespace-nowrap">
              {stat.label}
            </span>
            <span
              className={`text-sm font-bold tabular-nums leading-none flex items-center gap-1 shrink-0 ${
                stat.colored
                  ? isUp ? 'text-green-400' : isDown ? 'text-red-400' : 'text-primary-foreground/50'
                  : stat.highlight
                    ? 'text-secondary'
                    : 'text-primary-foreground/70'
              }`}
            >
              {stat.colored && (
                isUp ? <TrendingUp className="h-3 w-3 shrink-0" /> : isDown ? <TrendingDown className="h-3 w-3 shrink-0" /> : <Minus className="h-3 w-3 shrink-0" />
              )}
              {stat.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GoldPriceTicker;
